import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import {
  base64ToFile,
  ImageCropperComponent,
  ImageTransform,
} from 'ngx-image-cropper';
import { ImageModalData } from '@core/models';
import { FileUploadHelper } from '@core/helpers';
import { NgClass } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';

@Component({
  imports: [
    MatDialogModule,
    MatIconModule,
    ImageCropperComponent,
    NgClass,
    MatButtonModule,
  ],
  standalone: true,
  selector: 'app-image-upload-modal',
  styleUrls: ['./image-upload-modal.component.scss'],
  templateUrl: './image-upload-modal.component.html',
})
export class ImageUploadModalComponent implements OnInit {
  @ViewChild(ImageCropperComponent)
  private imageCropper!: ImageCropperComponent;
  public imageChangedEvent: Event | null = null;
  public canvasRotation = 0;
  public scale = 1;
  public transform: ImageTransform = {};
  public uploadFailed = false;
  private fileName: string | null = null;
  private imageDeleted = false;
  imageExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.bmp'];

  constructor(
    private dialogRef: MatDialogRef<ImageUploadModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ImageModalData,
  ) {}

  ngOnInit(): void {
    if (!this.data.imageType) this.data.imageType = this.imageExtensions[0];
    this.dialogRef.disableClose = true;
  }

  fileChangeEvent(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    if (FileUploadHelper.checkExtension(file.type, this.imageExtensions)) {
      // Clear out the previous image URL so the cropper focuses on the new file upload
      this.data.imageUrl = null;

      this.imageChangedEvent = event;
      this.fileName = file.name.replace(/\s/g, '');
      this.imageDeleted = false;
      this.uploadFailed = false;
      return;
    }

    this.uploadFailed = true;
  }

  loadImageFailed(): void {
    this.uploadFailed = true;
  }

  /**
   * Passes image to upload service
   */
  processFile(): void {
    let croppedImage: string | null = null;
    let file: File | null = null;

    if (
      (this.imageChangedEvent || this.data.imageUrl) &&
      !this.imageDeleted &&
      this.imageCropper
    ) {
      // 1. Take a safe crop snapshot from the fully operational background canvas
      const cropResult = this.imageCropper.crop();

      // 2. Strict guard check: Stop if base64 conversion fails
      if (cropResult && cropResult.base64) {
        croppedImage = cropResult.base64;

        // 3. Prevent crashing if editing an existing URL where filename was never set
        if (!this.fileName) {
          this.fileName = `cropped_image_${Date.now()}.png`;
        }

        try {
          const blob = base64ToFile(croppedImage);
          file = new File([blob], this.fileName, { type: blob.type });
        } catch (error) {
          console.error(
            'Failed to convert base64 image data to a file object:',
            error,
          );
          this.uploadFailed = true;
          return;
        }
      } else {
        console.error('Cropper failed to generate base64 data.');
        this.uploadFailed = true;
        return;
      }
    }

    this.dialogRef.close({
      file,
      croppedImage,
      fileName: this.fileName,
      imageDeleted: this.imageDeleted,
    });
  }

  resetImage(): void {
    this.scale = 1;
    this.canvasRotation = 0;
    this.transform = {};
  }

  deleteImage(): void {
    this.imageChangedEvent = null;
    this.imageDeleted = true;
    this.data.imageUrl = null;
    this.fileName = null;
  }

  rotateLeft(): void {
    this.canvasRotation--;
  }

  rotateRight(): void {
    this.canvasRotation++;
  }

  zoomOut(): void {
    this.scale = Math.max(0.1, +(this.scale - 0.1).toFixed(2));
    this.transform = { ...this.transform, scale: this.scale };
  }

  zoomIn(): void {
    this.scale = Math.min(3, +(this.scale + 0.1).toFixed(2));
    this.transform = { ...this.transform, scale: this.scale };
  }

  close(): void {
    this.dialogRef.close();
  }

  get title(): string {
    let title = this.data.imageType.replace(/_/g, ' ');
    title = title.charAt(0).toUpperCase() + title.slice(1);
    title += ' Image';
    return title;
  }
}
